import { assertPresetAllowed, awaitPresetAllowed } from '../access.js';
import { presetActionReceipt } from '../analytics.js';
import type { Preset } from '@studio/models/control/preset';
import { StatusDeviceNativePresetMode } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { AddPreset } from './types.js';
import { locateProxy } from '#~/agent/index.js';
import { uploadThumbnail } from '#~/control/preset/index.js';
import {
  finishPresetSave,
  freezePresetDevice,
  pendingPresetId,
  persistPresetMapping,
  runNativePresetSave,
  serializeDevicePresetSave
} from '#~/control/preset/native-owner.js';
import { StudioError } from '#~/error/index.js';

/**
 * Add a position preset for the specified device.
 *
 * Native SET runs before persist. Absolute mode stores position and issues
 * zero native SETs. Thumbnail failure must not allocate a second SET.
 */
export const addPreset: AddPreset = async (
  deviceId,
  groupId,
  initial,
  token
): Promise<Preset['id']> => {
  // The owner check in runNativePresetSave reads capabilities; wait for them.
  await awaitPresetAllowed(deviceId);
  const accepted = presetActionReceipt(deviceId, 'save');
  try {
    return await serializeDevicePresetSave(deviceId, async () => {
      const proxy = locateProxy(deviceId);

      if (!proxy) {
        throw new Error('CP-914');
      }

      const frozen = freezePresetDevice(proxy, deviceId);
      const assertDevice: typeof frozen = () => {
        assertPresetAllowed(deviceId);
        frozen();
      };
      assertDevice();

      const presetId = pendingPresetId(
        deviceId,
        groupId,
        initial.name,
        initial.rank
      ) as Preset['id'];
      const { result, position } = await runNativePresetSave(
        deviceId,
        presetId,
        initial.name,
        undefined,
        token
      );

      assertDevice();
      if (result?.failure && !result.completed) {
        throw new StudioError(result.failure);
      }

      // The correlated receipt chooses the one representation persisted for this shot.
      const useNative =
        result?.completed &&
        result.mode === StatusDeviceNativePresetMode.NATIVE_PRESET_MODE_NATIVE &&
        result.target;

      if (!useNative && !position) {
        throw new StudioError('Unable to save preset without a position or native target.');
      }

      const captured = {
        group: groupId,
        name: initial.name,
        rank: initial.rank,
        nativeTarget: useNative ? result.target : undefined,
        position: useNative ? undefined : position,
        requiredAxes: useNative ? 0 : result?.requiredAxes
      };

      // Upload may outlive the device/configuration. Publish the captured pair
      // only after revalidation, so refusal cannot leave a stale shared preset.
      let thumbnail: string;
      try {
        thumbnail = await uploadThumbnail(proxy.id, deviceId, presetId, token);
        assertDevice();
      } catch (cause) {
        assertDevice();
        // A thumbnail failure still preserves a confirmed camera SET for the
        // existing storage retry flow, provided its owner has not changed.
        await persistPresetMapping(proxy, deviceId, presetId, captured);
        assertDevice();
        await proxy.save(token);
        assertDevice();
        accepted();
        throw new StudioError('Unable to upload preset thumbnail.', { cause });
      }

      assertDevice();
      await persistPresetMapping(proxy, deviceId, presetId, { ...captured, thumbnail });
      assertDevice();
      await proxy.save(token);
      assertDevice();
      finishPresetSave(deviceId, presetId);
      accepted();
      return presetId;
    });
  } catch (cause) {
    if (cause instanceof StudioError) {
      throw cause;
    }
    throw new StudioError('Unable to add preset.', { cause });
  }
};
